import React, {useMemo, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {
    Camera,
    ChevronRight,
    Clapperboard,
    Gauge,
    HardDrive,
    Info,
    Monitor,
    ShieldCheck,
    Sparkles,
    Wifi,
} from 'lucide-react';
import {Link} from 'react-router-dom';

type Tab = 'monitoring' | 'mesh' | 'antivirus';
type Resolution = '4MP' | '5MP' | '6MP' | '8MP' | '12MP' | 'PTZ';

const CAM_PRICE: Record<Resolution, number> = {
    '4MP': 350, '5MP': 400, '6MP': 450, '8MP': 999, '12MP': 1900, PTZ: 6500,
};
const BITRATE: Record<Resolution, number> = {
    '4MP': 3, '5MP': 4, '6MP': 5, '8MP': 6, '12MP': 8, PTZ: 7,
};
const DISKS = [
    {size: 1, price: 740}, {size: 2, price: 820}, {size: 4, price: 900},
    {size: 8, price: 1535}, {size: 10, price: 2000}, {size: 12, price: 2400},
    {size: 16, price: 3650}, {size: 18, price: 4450},
    {size: 22, price: 4400}
];
const PROJECT = 500;
const MOUNT_PER = 250;
const JBOX = 100;
const CONFIG = 400;
const MESH = {router: 950, ap: 650, install: 1200, range: 45};
const AV = {install: 150, license: 350, min: 3};

const r100 = (v: number) => Math.ceil(v / 100) * 100;
const fmt = (v: number) => v.toLocaleString('pl-PL');

function nvrPrice(n: number) {
    if (n <= 0) return 0;
    if (n <= 4) return 250;
    if (n <= 8) return 350;
    if (n <= 16) return 500;
    if (n <= 32) return 2000;
    if (n <= 64) return 3700;
    return 6500;
}

function pickDisk(tb: number) {
    if (tb <= 0) return {label: '-', cost: 0, tb: 0};
    const max = DISKS[DISKS.length - 1];
    if (tb <= max.size) {
        const d = DISKS.find((d) => d.size >= tb)!;
        return {label: `${d.size} TB`, cost: d.price, tb: +(tb.toFixed(2))};
    }
    const cnt = Math.ceil(tb / max.size);
    return {label: `${cnt}× ${max.size} TB`, cost: cnt * max.price, tb: +(tb.toFixed(2))};
}

function Pills<T extends string>({options, value, onChange, id = 'p'}: {
    options: { key: T; label: string }[]; value: T; onChange: (v: T) => void; id?: string;
}) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((o) => (
                <button key={o.key} onClick={() => onChange(o.key)}
                        className={`relative px-4 py-2 rounded-xl text-sm font-semibold transition-all overflow-hidden ${
                            value === o.key ? 'text-white shadow-lg shadow-primary/20' : 'bg-muted/80 text-muted-foreground hover:bg-muted'}`}>
                    {value === o.key && (
                        <motion.div layoutId={`${id}-bg`}
                                    className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark rounded-xl"
                                    transition={{type: 'spring', bounce: 0.15, duration: 0.5}}/>
                    )}
                    <span className="relative z-10">{o.label}</span>
                </button>
            ))}
        </div>
    );
}

function Card({icon: Icon, title, children, accent}: {
    icon: React.ElementType; title: string; children: React.ReactNode; accent?: boolean;
}) {
    return (
        <div className={`rounded-3xl p-5 md:p-6 space-y-5 ${
            accent ? 'bg-gradient-to-br from-primary/5 via-transparent to-primary/5 dark:from-primary/10 dark:to-primary/5 border border-primary/15'
                : 'bg-card/80 backdrop-blur-sm border border-border/40'}`}>
            <div className="flex items-center gap-3">
                <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent ? 'bg-primary/15' : 'bg-muted'}`}>
                    <Icon size={20} className="text-primary"/>
                </div>
                <h3 className="font-bold text-base md:text-lg text-foreground">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function Slider({label, value, min, max, unit, onChange}: {
    label: string; value: number; min: number; max: number; unit?: string; onChange: (v: number) => void;
}) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">{label}</span>
                <span className="text-sm font-bold text-primary tabular-nums">{value}{unit && ` ${unit}`}</span>
            </div>
            <input type="range" min={min} max={max} value={value}
                   onChange={(e) => onChange(parseInt(e.target.value))}
                   className="w-full h-2 bg-border rounded-full appearance-none cursor-pointer accent-primary"/>
            <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
                <span>{min}</span><span>{max}</span>
            </div>
        </div>
    );
}

export function CameraCalculator() {
    const [tab, setTab] = useState<Tab>('monitoring');

    const [cams, setCams] = useState(4);
    const [res, setRes] = useState<Resolution>('4MP');
    const [days, setDays] = useState(14);
    const [fps, setFps] = useState(15);

    const [meshMode, setMeshMode] = useState<'area' | 'manual'>('area');
    const [meshArea, setMeshArea] = useState(120);
    const [meshAPs, setMeshAPs] = useState(2);

    const [avStations, setAvStations] = useState(5);

    const mon = useMemo(() => {
        const storageGB = (BITRATE[res] * fps / 15) * 86400 * days * cams / (8 * 1024);
        const disk = pickDisk(storageGB / 1024);
        const camCost = cams * CAM_PRICE[res];
        const nvr = nvrPrice(cams);
        const mountCost = PROJECT + cams * (MOUNT_PER + JBOX) + CONFIG;
        const raw = camCost + nvr + disk.cost + mountCost;
        return {camCost, nvr, disk, mountCost, total: r100(raw)};
    }, [cams, res, days, fps]);

    const mesh = useMemo(() => {
        const aps = meshMode === 'area' ? Math.max(1, Math.ceil(meshArea / MESH.range)) : meshAPs;
        const raw = MESH.router + aps * MESH.ap + MESH.install;
        return {aps, total: r100(raw)};
    }, [meshMode, meshArea, meshAPs]);

    const av = useMemo(() => {
        const count = Math.max(AV.min, avStations);
        const raw = count * (AV.install + AV.license);
        return {count, total: r100(raw)};
    }, [avStations]);

    const activeTotal = tab === 'monitoring' ? mon.total : tab === 'mesh' ? mesh.total : av.total;

    const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
        {key: 'monitoring', label: 'Kamery', icon: Camera},
        {key: 'mesh', label: 'Sieć MESH', icon: Wifi},
        {key: 'antivirus', label: 'Antywirus', icon: ShieldCheck},
    ];

    return (
        <section id="kalkulator" className="w-full scroll-mt-20 py-20 md:py-32 px-5 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute -top-40 left-1/2 -translate-x-1/2 w-[min(800px,150vw)] h-[min(800px,150vw)] rounded-full bg-primary/[0.04] dark:bg-primary/[0.06] blur-[120px]"/>
            </div>

            <div className="relative max-w-6xl mx-auto">
                <motion.div initial={{opacity: 0, y: 30}} whileInView={{opacity: 1, y: 0}} viewport={{once: true}}
                            className="flex flex-col items-center text-center mb-10 gap-4">
                    <div
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 dark:bg-primary/15 text-primary text-xs md:text-sm font-semibold">
                        <Sparkles size={14}/> Kalkulator kosztów monitoringu - Mielec i promień do 250 km
                    </div>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
                        Sprawdź ile kosztuje <span className="gradient-text">Twój system</span>
                    </h2>
                    <p className="text-muted-foreground max-w-lg text-sm md:text-base">
                        Dobierz liczbę kamer, rozdzielczość i opcje dodatkowe. Od razu zobaczysz szacunkowy koszt
                        montażu monitoringu w&nbsp;Mielcu i okolicach do 250 km. Bez zobowiązań, bez czekania.
                    </p>
                </motion.div>

                <div className="flex justify-center mb-10">
                    <div
                        className="inline-flex gap-1 p-1.5 rounded-2xl bg-card/70 backdrop-blur-xl border border-border/40 shadow-lg shadow-black/[0.03] dark:shadow-black/20">
                        {TABS.map(({key, label, icon: Icon}) => (
                            <button key={key} onClick={() => setTab(key)}
                                    className="relative px-4 sm:px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer select-none">
                                {tab === key && (
                                    <motion.div layoutId="calc-tab"
                                                className="absolute inset-0 bg-primary rounded-xl"
                                                style={{boxShadow: '0 0 20px rgba(94,40,255,0.25)'}}
                                                transition={{type: 'spring', stiffness: 350, damping: 30}}/>
                                )}
                                <span
                                    className={`relative z-10 flex items-center gap-1.5 transition-colors duration-200 ${
                                        tab === key ? 'text-white' : 'text-muted-foreground'}`}>
                  <Icon size={15}/>{label}
                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
                    <div className="lg:col-span-7">
                        <AnimatePresence mode="wait">
                            {tab === 'monitoring' && (
                                <motion.div key="mon" initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}}
                                            exit={{opacity: 0, y: -10}} transition={{duration: 0.25}}
                                            className="space-y-5">
                                    <Card icon={Camera} title="Kamery" accent>
                                        <div className="space-y-5">
                                            <Slider label="Liczba kamer" value={cams} min={1} max={64}
                                                    onChange={setCams}/>
                                            <div>
                                                <p className="text-sm font-medium text-muted-foreground mb-2">Rozdzielczość</p>
                                                <Pills id="res" options={[
                                                    {key: '4MP' as const, label: '4 MP'}, {
                                                        key: '5MP' as const,
                                                        label: '5 MP'
                                                    },
                                                    {key: '6MP' as const, label: '6 MP'}, {
                                                        key: '8MP' as const,
                                                        label: '8 MP'
                                                    },
                                                    {key: '12MP' as const, label: '12 MP'}, {
                                                        key: 'PTZ' as const,
                                                        label: 'PTZ'
                                                    },
                                                ]} value={res} onChange={setRes}/>
                                            </div>
                                        </div>
                                    </Card>

                                    <Card icon={Clapperboard} title="Nagrywanie">
                                        <div className="space-y-5">
                                            <Slider label="Okres przechowywania" value={days} min={7} max={90}
                                                    unit="dni" onChange={setDays}/>
                                            <div>
                                                <p className="text-sm font-medium text-muted-foreground mb-2">Klatki na
                                                    sekundę</p>
                                                <Pills id="fps" options={[
                                                    {key: '10', label: '10 FPS'}, {
                                                        key: '15',
                                                        label: '15 FPS'
                                                    }, {key: '25', label: '25 FPS'},
                                                ]} value={String(fps)} onChange={(v) => setFps(parseInt(v))}/>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            )}

                            {tab === 'mesh' && (
                                <motion.div key="mesh" initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}}
                                            exit={{opacity: 0, y: -10}} transition={{duration: 0.25}}
                                            className="space-y-5">
                                    <div
                                        className="flex items-start gap-3 p-4 rounded-2xl bg-blue-500/[0.08] dark:bg-blue-500/10 border border-blue-500/15">
                                        <Info size={16} className="text-blue-500 mt-0.5 shrink-0"/>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            Korzystamy z profesjonalnych urządzeń <span
                                            className="font-bold text-foreground">Ubiquiti</span> - niezawodna sieć MESH
                                            dla domu i firmy.
                                        </p>
                                    </div>
                                    <Card icon={Wifi} title="Konfiguracja sieci" accent>
                                        <div className="space-y-5">
                                            <div>
                                                <p className="text-sm font-medium text-muted-foreground mb-2">Sposób
                                                    doboru</p>
                                                <Pills id="mmode" options={[
                                                    {key: 'area' as const, label: 'Podaj metraż'},
                                                    {key: 'manual' as const, label: 'Wybierz ilość AP'},
                                                ]} value={meshMode} onChange={setMeshMode}/>
                                            </div>
                                            {meshMode === 'area' ? (
                                                <>
                                                    <Slider label="Powierzchnia budynku" value={meshArea} min={30}
                                                            max={500} unit="m²" onChange={setMeshArea}/>
                                                    <p className="text-xs text-muted-foreground">
                                                        Dla <span
                                                        className="font-semibold text-foreground">{meshArea} m²</span> rekomendujemy{' '}
                                                        <span
                                                            className="font-semibold text-primary">{Math.max(1, Math.ceil(meshArea / MESH.range))} Access Point{mesh.aps !== 1 ? 'ów' : ''}</span>{' '}
                                                        (1 AP co ~{MESH.range} m²)
                                                    </p>
                                                </>
                                            ) : (
                                                <Slider label="Liczba Access Pointów" value={meshAPs} min={1} max={10}
                                                        onChange={setMeshAPs}/>
                                            )}
                                        </div>
                                    </Card>
                                </motion.div>
                            )}

                            {tab === 'antivirus' && (
                                <motion.div key="av" initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}}
                                            exit={{opacity: 0, y: -10}} transition={{duration: 0.25}}
                                            className="space-y-5">
                                    <div
                                        className="flex items-start gap-3 p-4 rounded-2xl bg-emerald-500/[0.08] dark:bg-emerald-500/10 border border-emerald-500/15">
                                        <Info size={16} className="text-emerald-500 mt-0.5 shrink-0"/>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            Korzystamy z licencji <span
                                            className="font-bold text-foreground">ESET</span> - sprawdzona ochrona dla
                                            firm. Instalacja możliwa od {AV.min} stanowisk.
                                        </p>
                                    </div>
                                    <Card icon={ShieldCheck} title="Ochrona antywirusowa" accent>
                                        <div className="space-y-5">
                                            <Slider label="Liczba stanowisk" value={avStations} min={AV.min} max={50}
                                                    onChange={setAvStations}/>
                                            <div
                                                className="flex items-start gap-3 p-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/10">
                                                <ShieldCheck size={16} className="text-emerald-500 mt-0.5 shrink-0"/>
                                                <p className="text-xs text-muted-foreground">
                                                    <span
                                                        className="font-semibold text-emerald-600 dark:text-emerald-400">Gratis</span> -
                                                    sprawdzenie zabezpieczenia sieciowego u klienta
                                                </p>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="sticky top-24 space-y-5">
                            <AnimatePresence mode="wait">
                                {tab === 'monitoring' && (
                                    <motion.div key="h-mon" initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}}
                                                exit={{opacity: 0, y: -20}} transition={{duration: 0.25}}>
                                        <div className="relative rounded-3xl overflow-hidden">
                                            <div
                                                className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-blue-600"/>
                                            <div
                                                className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.12),transparent_60%)]"/>
                                            <div className="relative p-6 text-white">
                                                <div className="flex items-center gap-3 mb-5">
                                                    <div
                                                        className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                                                        <HardDrive size={24}/>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-lg leading-tight">Rekomendowany
                                                            dysk</h4>
                                                        <p className="text-white/60 text-sm">{cams} kamer × {res}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-end justify-between">
                                                    <div>
                                                        <p className="text-4xl font-extrabold tracking-tight">{mon.disk.label}</p>
                                                        <p className="text-white/50 text-xs mt-1">Wymagane: {mon.disk.tb} TB</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                {tab === 'mesh' && (
                                    <motion.div key="h-mesh" initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}}
                                                exit={{opacity: 0, y: -20}} transition={{duration: 0.25}}>
                                        <div className="relative rounded-3xl overflow-hidden">
                                            <div
                                                className="absolute inset-0 bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-600"/>
                                            <div
                                                className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.12),transparent_60%)]"/>
                                            <div className="relative p-6 text-white">
                                                <div className="flex items-center gap-3 mb-5">
                                                    <div
                                                        className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                                                        <Wifi size={24}/></div>
                                                    <div>
                                                        <h4 className="font-bold text-lg leading-tight">Twoja sieć
                                                            MESH</h4>
                                                        <p className="text-white/60 text-sm">Ubiquiti</p>
                                                    </div>
                                                </div>
                                                <p className="text-4xl font-extrabold tracking-tight">1 Router
                                                    + {mesh.aps} AP</p>
                                                <p className="text-white/50 text-xs mt-1">
                                                    {meshMode === 'area' ? `${meshArea} m² - 1 AP co ~${MESH.range} m²` : `${mesh.aps} Access Pointów`}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                {tab === 'antivirus' && (
                                    <motion.div key="h-av" initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}}
                                                exit={{opacity: 0, y: -20}} transition={{duration: 0.25}}>
                                        <div className="relative rounded-3xl overflow-hidden">
                                            <div
                                                className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600"/>
                                            <div
                                                className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.12),transparent_60%)]"/>
                                            <div className="relative p-6 text-white">
                                                <div className="flex items-center gap-3 mb-5">
                                                    <div
                                                        className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                                                        <Monitor size={24}/></div>
                                                    <div>
                                                        <h4 className="font-bold text-lg leading-tight">Ochrona
                                                            ESET</h4>
                                                        <p className="text-white/60 text-sm">Licencja + instalacja</p>
                                                    </div>
                                                </div>
                                                <p className="text-4xl font-extrabold tracking-tight">{av.count} stanowisk</p>
                                                <p className="text-white/50 text-xs mt-1">Audyt zabezpieczeń sieci w
                                                    cenie</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="rounded-3xl bg-card/80 backdrop-blur-sm border border-border/40 p-6">
                                <h4 className="font-bold text-lg mb-5 text-foreground flex items-center gap-2">
                                    <Gauge size={18} className="text-primary"/> Podsumowanie
                                </h4>
                                <div className="space-y-2.5 text-sm">
                                    {tab === 'monitoring' && (<>
                                        <div
                                            className="flex items-start gap-3 p-3 rounded-xl bg-primary/[0.06] border border-primary/10">
                                            <Sparkles size={16} className="text-primary mt-0.5 shrink-0"/>
                                            <p className="text-xs text-muted-foreground">
                                                W zestawie <span
                                                className="font-semibold text-foreground">kamery AI</span> z
                                                inteligentną analityką obrazu, detekcja ruchu, rozpoznawanie osób i
                                                pojazdów, powiadomienia w czasie rzeczywistym.
                                            </p>
                                        </div>
                                        <div
                                            className="flex items-start gap-3 p-3 rounded-xl bg-primary/[0.06] border border-primary/10">
                                            <Camera size={16} className="text-primary mt-0.5 shrink-0"/>
                                            <p className="text-xs text-muted-foreground">
                                                Wszystkie kamery posiadają <span
                                                className="font-semibold text-foreground">wbudowany mikrofon</span> -
                                                nagrywanie obrazu i dźwięku w jednym urządzeniu.
                                            </p>
                                        </div>
                                        <ul className="space-y-1.5 text-xs text-muted-foreground pt-2">
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>{cams}×
                                                kamera {res}</li>
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>Rejestrator NVR
                                            </li>
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>Dysk {mon.disk.label}
                                            </li>
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>Montaż,
                                                konfiguracja i projekt
                                            </li>
                                        </ul>
                                    </>)}
                                    {tab === 'mesh' && (
                                        <ul className="space-y-1.5 text-xs text-muted-foreground">
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>Router sieciowy
                                            </li>
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>Access Pointy
                                                - {mesh.aps} szt.
                                            </li>
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>Wykonanie i
                                                konfiguracja systemu
                                            </li>
                                        </ul>
                                    )}
                                    {tab === 'antivirus' && (
                                        <ul className="space-y-1.5 text-xs text-muted-foreground">
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>Licencje ESET
                                                - {av.count} szt.
                                            </li>
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-primary shrink-0"/>Instalacja na
                                                stanowiskach
                                            </li>
                                            <li className="flex items-center gap-2"><span
                                                className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"/><span
                                                className="text-emerald-600 dark:text-emerald-400 font-semibold">Gratis</span> -
                                                audyt zabezpieczeń sieci
                                            </li>
                                        </ul>
                                    )}

                                    <div className="border-t border-border/60 pt-4 mt-4">
                                        <div className="flex items-end justify-between">
                                            <span className="font-bold text-lg text-foreground">Szacunkowy koszt</span>
                                            <span className="text-3xl font-extrabold gradient-text tracking-tight">
                        {fmt(activeTotal)} zł
                      </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="flex items-start gap-3 p-4 rounded-2xl bg-amber-500/[0.12] dark:bg-amber-500/15 border border-amber-500/30">
                                <Info size={20} className="text-amber-500 mt-0.5 shrink-0"/>
                                <div className="space-y-1.5">
                                    <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 leading-relaxed">
                                        Ceny szacunkowe netto. Dokładna wycena po wizji lokalnej. Skontaktuj się z nami!
                                    </p>
                                    <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
                                        Treści i&nbsp;ceny prezentowane na stronie mają charakter poglądowy i&nbsp;nie
                                        stanowią oferty handlowej w&nbsp;rozumieniu art.&nbsp;66 §&nbsp;1 Kodeksu
                                        cywilnego. Każda współpraca wymaga indywidualnego potwierdzenia
                                        i&nbsp;podpisania umowy lub złożenia zamówienia.
                                    </p>
                                </div>
                            </div>

                            <Link to="/wycena"
                                  className="group w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-primary text-white font-bold text-base hover:brightness-110 transition-all shadow-lg shadow-primary/20">
                                Zamów bezpłatną wycenę
                                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform"/>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
